import React, { RefObject, useCallback, useRef, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { Nexset } from 'new/entities/Nexset';
import styles from '../DataProducts/DataProducts.module.scss';
import { Box } from 'new/elements/Box';
import DateUtils from 'js/lib/DateUtils';
import { ProfileImage } from 'new/elements/ProfileImage';
import { Button } from 'new/elements/Button';
import { DomainItemActions } from './DomainItemActions';
import ItemRating from './ItemRating';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';

interface DomainItemCardProps {
  onClickDetails: (item: Nexset) => void;
  item: Nexset;
  domainId: number;
  timeoutEnterRef: any;
  overlayRef: RefObject<HTMLDivElement>;
}

const DomainItemCard = (props: DomainItemCardProps) => {
  const { item, overlayRef, domainId, timeoutEnterRef, onClickDetails } = props;
  const [showButtons, setShowButtons] = useState(false);
  const timeoutLeaveRef = useRef(null);
  const heightRef = useRef(0);
  const paperRef = useRef<HTMLDivElement>(null);

  const onMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      clearTimeout(timeoutEnterRef.current);
      clearTimeout(timeoutLeaveRef.current);
      timeoutEnterRef.current = setTimeout(() => {
        setShowButtons(true);
        if (paperRef.current && overlayRef.current) {
          overlayRef.current.className += ' ' + styles.overlay_hover;
          overlayRef.current.style.zIndex = '4';
          paperRef.current.style.zIndex = '5';
          heightRef.current = paperRef.current.offsetHeight;
          paperRef.current.style.width = paperRef.current.offsetWidth + 'px';
          paperRef.current.style.height = 252 + 'px';
          paperRef.current.style.position = 'absolute';
        }
      }, 500);
    },
    [overlayRef],
  );

  const onMouseLeave = useCallback(
    (event: any) => {
      clearTimeout(timeoutEnterRef.current);
      timeoutLeaveRef.current = setTimeout(() => {
        if (paperRef.current && overlayRef.current) {
          overlayRef.current.className = styles.overlay;
          setShowButtons(false);
          paperRef.current.style.height = '208px';
          paperRef.current.style.width = 'unset';
          paperRef.current.style.position = 'relative';
          paperRef.current.style.zIndex = 'unset';
        }
      }, 500);
    },
    [overlayRef, timeoutEnterRef],
  );

  const onClick = () => {
    onClickDetails(item);
  };

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={paperRef}
      className={styles.itemCard}
    >
      <Stack flex={1} justifyContent="space-between">
        <Stack flex={1} padding="12px">
          <Stack flex={1}>
            <Typography className={styles.title} variant="h6">
              {item.name}
            </Typography>
            <Typography className={styles.description} variant="body2">
              {item.description}
            </Typography>
          </Stack>
          <Stack gap="8px">
            <Stack className={styles.cardInfo}>
              <Typography variant="overline" className={styles.labelInfo}>
                RATING
              </Typography>
              <ItemRating item={item} domainId={domainId} />
            </Stack>
            <Stack className={styles.cardInfo}>
              <Typography variant="overline" className={styles.labelInfo}>
                LAST REFRESHED
              </Typography>
              <Typography variant="body2">
                {DateUtils.getDateString(item?.updated_at, 'MMM DD, YYYY')}
              </Typography>
            </Stack>
            <Stack className={styles.cardInfo}>
              <Typography variant="overline" className={styles.labelInfo}>
                NEXSET ID
              </Typography>
              <ChipWithCopy tooltipTitle="Copy Nexset ID" toCopy={item?.id}>
                {item?.id}
              </ChipWithCopy>
            </Stack>
            <Stack className={styles.cardInfo}>
              <Typography variant="overline" className={styles.labelInfo}>
                OWNER
              </Typography>
              <Stack direction="row" gap="4px" alignItems="center">
                <ProfileImage
                  color={'rgba(0,0,0,0.5)'}
                  size="xxsmall"
                  fullName={item.owner?.full_name}
                />
                <Typography variant="body2">{item.owner?.full_name}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        {showButtons ? (
          <Stack
            className={styles.actions}
            direction="row"
            gap="16px"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button variant="outlined" color="primary" onClick={onClick}>
              View Details
            </Button>
            <DomainItemActions domainId={domainId} item={item} />
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

export default DomainItemCard;
